import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import CountrySearchSelect from '../../common/CountrySearchSelect';
import { useCountries } from '../../../hooks/useCountries';
import { apiGet, apiPut } from '../../../services/api';

const DESCRIPTION_MAX = 500;
const EMPLOYEE_COUNT_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '500+'];

const INITIAL_FORM = {
  name: '',
  legal_name: '',
  website: '',
  country: '',
  city: '',
  address: '',
  description: '',
  employee_count: '',
  vat_id: '',
  vat_country: '',
};

function vatCountryNameFromIso(countries, isoCode) {
  if (!isoCode || countries.length === 0) return '';
  const matched = countries.find((c) => c.code === isoCode.toUpperCase());
  return matched ? matched.name : '';
}

export default function CompanySettingsTab({ token }) {
  const { countries, loading: countriesLoading } = useCountries();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(INITIAL_FORM);
  const [vatCountryName, setVatCountryName] = useState('');

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function loadProfile() {
      setLoading(true);
      setError('');
      try {
        const res = await apiGet('/api/v1/company/profile', token);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(data?.message || 'Could not load company profile.');
          return;
        }
        const profile = data?.data ?? {};
        setForm({
          name: profile.name ?? '',
          legal_name: profile.legal_name ?? '',
          website: profile.website ?? '',
          country: profile.country ?? '',
          city: profile.city ?? '',
          address: profile.address ?? '',
          description: profile.description ?? '',
          employee_count: profile.employee_count ?? '',
          vat_id: profile.vat_id ?? '',
          vat_country: profile.vat_country ?? '',
        });
      } catch {
        if (!cancelled) {
          setError('Could not load company profile.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    setVatCountryName(vatCountryNameFromIso(countries, form.vat_country));
  }, [countries, form.vat_country]);

  const descriptionLength = form.description.length;
  const showVatCountry = Boolean(form.vat_id.trim());
  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!token) return;

    setSaving(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    try {
      const payload = {
        name: form.name.trim(),
        legal_name: form.legal_name.trim(),
        website: form.website.trim(),
        country: form.country.trim(),
        city: form.city.trim(),
        address: form.address.trim(),
        description: form.description.trim(),
        employee_count: form.employee_count || null,
        vat_id: form.vat_id.trim() || null,
        vat_country: form.vat_id.trim() ? form.vat_country.trim().toUpperCase() : null,
      };

      const res = await apiPut('/api/v1/company/profile', payload, token);
      const data = await res.json().catch(() => ({}));

      if (res.status === 422 && data?.errors) {
        setFieldErrors(data.errors);
        return;
      }
      if (!res.ok) {
        setError(data?.message || 'Could not update profile. Please try again.');
        return;
      }

      const profile = data?.data ?? payload;
      setForm((prev) => ({
        ...prev,
        ...payload,
        ...{
          name: profile.name ?? payload.name,
          legal_name: profile.legal_name ?? payload.legal_name,
          website: profile.website ?? payload.website,
          country: profile.country ?? payload.country,
          city: profile.city ?? payload.city,
          address: profile.address ?? payload.address,
          description: profile.description ?? payload.description,
          employee_count: profile.employee_count ?? payload.employee_count ?? '',
          vat_id: profile.vat_id ?? payload.vat_id ?? '',
          vat_country: profile.vat_country ?? payload.vat_country ?? '',
        },
      }));
      setSuccess('Profile updated successfully.');
    } catch {
      setError('Could not update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status" data-testid="rwm-settings-loading" />
      </div>
    );
  }

  return (
    <div className="rwm-settings__form">
      {success ? (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccess('')}
          data-testid="rwm-settings-success"
        >
          {success}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger" data-testid="rwm-settings-error">
          {error}
        </Alert>
      ) : null}

      <Form onSubmit={handleSubmit} noValidate>
        <h3 className="h6 rwm-settings__section-title">Company Info</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Company Name *</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                maxLength={255}
                required
                data-testid="rwm-settings-name"
              />
              {fieldErrors.name ? <div className="rwm-auth__field-error mt-1">{fieldErrors.name[0]}</div> : null}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Legal Name *</Form.Label>
              <Form.Control
                value={form.legal_name}
                onChange={(e) => setField('legal_name', e.target.value)}
                maxLength={255}
                required
                data-testid="rwm-settings-legal_name"
              />
              {fieldErrors.legal_name ? (
                <div className="rwm-auth__field-error mt-1">{fieldErrors.legal_name[0]}</div>
              ) : null}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Website *</Form.Label>
          <Form.Control
            type="url"
            value={form.website}
            onChange={(e) => setField('website', e.target.value)}
            maxLength={500}
            required
            data-testid="rwm-settings-website"
          />
          {fieldErrors.website ? <div className="rwm-auth__field-error mt-1">{fieldErrors.website[0]}</div> : null}
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Country *</Form.Label>
              <CountrySearchSelect
                countries={countries}
                loading={countriesLoading}
                value={form.country}
                onChange={(value) => setField('country', value)}
                invalid={Boolean(fieldErrors.country)}
              />
              {fieldErrors.country ? <div className="rwm-auth__field-error mt-1">{fieldErrors.country[0]}</div> : null}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>City *</Form.Label>
              <Form.Control
                value={form.city}
                onChange={(e) => setField('city', e.target.value)}
                maxLength={100}
                required
                data-testid="rwm-settings-city"
              />
              {fieldErrors.city ? <div className="rwm-auth__field-error mt-1">{fieldErrors.city[0]}</div> : null}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Address *</Form.Label>
          <Form.Control
            value={form.address}
            onChange={(e) => setField('address', e.target.value)}
            maxLength={500}
            required
            data-testid="rwm-settings-address"
          />
          {fieldErrors.address ? <div className="rwm-auth__field-error mt-1">{fieldErrors.address[0]}</div> : null}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Employee Count</Form.Label>
          <Form.Select
            value={form.employee_count}
            onChange={(e) => setField('employee_count', e.target.value)}
            data-testid="rwm-settings-employee_count"
          >
            <option value="">Select employee range</option>
            {EMPLOYEE_COUNT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Form.Select>
          {fieldErrors.employee_count ? (
            <div className="rwm-auth__field-error mt-1">{fieldErrors.employee_count[0]}</div>
          ) : null}
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Description *</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            maxLength={DESCRIPTION_MAX}
            required
            data-testid="rwm-settings-description"
          />
          <div className="text-muted small mt-1" data-testid="rwm-settings-description-count">
            {descriptionLength}/{DESCRIPTION_MAX}
          </div>
          {fieldErrors.description ? (
            <div className="rwm-auth__field-error mt-1">{fieldErrors.description[0]}</div>
          ) : null}
        </Form.Group>

        <h3 className="h6 rwm-settings__section-title">VAT Information</h3>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>VAT ID</Form.Label>
              <Form.Control
                value={form.vat_id}
                onChange={(e) => setField('vat_id', e.target.value)}
                maxLength={50}
                data-testid="rwm-settings-vat_id"
              />
              {fieldErrors.vat_id ? <div className="rwm-auth__field-error mt-1">{fieldErrors.vat_id[0]}</div> : null}
            </Form.Group>
          </Col>

          {showVatCountry ? (
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>VAT Country</Form.Label>
                <div data-testid="rwm-settings-vat_country">
                  <CountrySearchSelect
                    countries={countries}
                    loading={countriesLoading}
                    value={vatCountryName}
                    onChange={(name) => {
                      setVatCountryName(name);
                      const matched = countries.find((c) => c.name === name);
                      setField('vat_country', matched ? matched.code : '');
                    }}
                    invalid={Boolean(fieldErrors.vat_country)}
                  />
                </div>
                {fieldErrors.vat_country ? (
                  <div className="rwm-auth__field-error mt-1">{fieldErrors.vat_country[0]}</div>
                ) : null}
              </Form.Group>
            </Col>
          ) : null}
        </Row>

        <Button
          type="submit"
          variant="info"
          disabled={saving}
          data-testid="rwm-settings-save"
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Form>
    </div>
  );
}
